/**
 * Created by rathompson on 12/14/23
 * File: ViewAcquisitionModal.tsx
 * Project: cloud-mgmt-modules ©
 * ©
 */


import type { VFC } from 'react'
import {
  Body,
  Bold,
  Button, ChipInput, Input,
  ModalBody, ModalCloseButton,
  ModalFooter,
  ModalHeader,
  ModalProps, noop, useIsMounted
} from '@panwds/react-ui'
import { useCallback, useContext, useMemo, useState } from 'react'
import {
  BUTTON_CANCEL_ACQUISITION, BUTTON_CLOSE, FORM_FIELD_ACQUISITION_KEY,
  FORM_FIELD_CONTACT_EMAILS,
  FORM_FIELD_TSG_ID, LABEL_ACQ_KEY_SUBTEXT, TITLE_PENDING_ACQ,
} from '../../constants/LabelConstants'
import { safeSet } from '../../../common/utils/Utils'
import TenancyService from '../../services/TenancyService'
import ScreenLoader from '../../../activation/components/common/ScreenLoader'
import { Modal } from '../../../common/components/modal/Modal'
import moment from 'moment'
import GlobalContext from '../../../common/context/GlobalContext'
import StringTemplate from '../../../common/utils/StringTemplate'

const ViewAcquisitionModal: VFC<ModalProps & {
  closeModal: any,
  acquisition: any,
  onDelete: any
}> = ({ isOpen, closeModal, acquisition, onDelete }) => {

  const isMounted = useIsMounted()
  const { treeReducer } = useContext(GlobalContext)
  const { selectedTreeNode } = treeReducer

  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const title = useMemo(() => {
    return StringTemplate(TITLE_PENDING_ACQ, [acquisition.target_tsg_id])
  }, [acquisition.target_tsg_id])

  const subText = useMemo(() => {
    switch (acquisition.status) {
      case 'approved':
        return ''
      case 'rejected':
        return ''
      case 'pending':
      default:
        return `Pending approval since ${moment(acquisition.request_timestamp).format('MM/DD/YYYY h:mm:ss a')}`
    }
  }, [acquisition.request_timestamp, acquisition.status])

  const items = acquisition.contacts.map((value: string) => ({ value }))

  const onCancelClick = useCallback(async () => {
    try {
      safeSet(setLoading, true, isMounted.current)
      await TenancyService.acquisitions.delete(selectedTreeNode.id, acquisition.id)
      closeModal()
      onDelete(acquisition)
    }
    catch (error: any) {
      safeSet(setError, error.message, isMounted.current)
    }
    finally {
      safeSet(setLoading, false, isMounted.current)
    }
  }, [acquisition, closeModal, isMounted, onDelete, selectedTreeNode.id])

  return (
    <Modal
      onClose={closeModal}
      { ...{ isOpen } }
    >
      <ModalHeader {...{ title }} />
      <ModalBody addClassName='cm-flex cm-flex-col cm-gap-2'>
        {loading && <ScreenLoader appearance='light'/>}
        {error && <Bold appearance='semibold' addClassName='tw-text-error'>{error}</Bold>}
        <Body size='md'>{subText}</Body>
        <div className='cm-flex cm-flex-col cm-gap-1'>
          <Body size='md' addClassName='cm-font-semibold cm-text-secondary'>{FORM_FIELD_TSG_ID}</Body>
          <Input
            name='tsg_id'
            readOnly
            allowCopy
            value={acquisition.target_tsg_id}
          />
        </div>
        <div className='cm-flex cm-flex-col cm-gap-1'>
          <Body size='md' addClassName='cm-font-semibold cm-text-secondary'>{FORM_FIELD_CONTACT_EMAILS}</Body>
          <ChipInput
            items={[]}
            selectedItems={items}
            onSelectedItemsChange={noop}
            disabled
          />
        </div>
        <Body size='md'>{LABEL_ACQ_KEY_SUBTEXT}</Body>
        <div className='cm-flex cm-flex-col cm-gap-1'>
          <Body size='md' addClassName='cm-font-semibold cm-text-secondary'>{FORM_FIELD_ACQUISITION_KEY}</Body>
          <Input
            name='acquisition_key'
            readOnly
            allowCopy
            value={acquisition.id}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        {acquisition.status === 'pending' && (
          <Button
            appearance="secondary-destructive"
            onClick={onCancelClick}
          >{BUTTON_CANCEL_ACQUISITION}</Button>
        )}
        <ModalCloseButton appearance='secondary'>{BUTTON_CLOSE}</ModalCloseButton>
      </ModalFooter>
    </Modal>
  )
}

export default ViewAcquisitionModal
